import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Lightbulb, Sparkles } from 'lucide-react';
import { AISuggestion } from '../../types';

const GoogleAdsIcon = () => <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none"><path d="M12.48 10.92v3.28h7.84c-.24 1.84-.84 3.18-1.74 4.1-.98.98-2.52 2.06-5.1 2.06-4.08 0-7.28-3.28-7.28-7.36s3.2-7.36 7.28-7.36c2.2 0 3.8.86 4.98 2l2.32-2.32C18.68 3.32 16.18 2 12.48 2 6.68 2 2 6.48 2 12s4.68 10 10.48 10c3.08 0 5.38-1.02 7.18-2.92 1.86-1.86 2.44-4.48 2.44-6.6 0-.66-.06-1.26-.16-1.76h-9.46z" fill="#4285F4"/></svg>;
const MetaAdsIcon = () => <img src="/logo_meta.png" alt="Meta" className="w-4 h-4" />;
const LinkedInAdsIcon = () => <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0A66C2"/></svg>;

interface AutomationAISuggestionsProps {
  suggestions: AISuggestion[];
  onCreateFromSuggestion: (suggestion: AISuggestion) => void;
}

const AutomationAISuggestions: React.FC<AutomationAISuggestionsProps> = ({ suggestions, onCreateFromSuggestion }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary-500 to-purple-500">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('automation.ai.suggestionsTitle', 'AI Suggestions')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('automation.ai.suggestionsDescription', 'Based on your campaign performance data')}</p>
          </div>
        </div>
        {suggestions.length === 0 ? (
          <div className="text-center py-8">
            <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">{t('automation.ai.noSuggestions', 'No suggestions available yet')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {suggestions.map((suggestion) => (
              <div key={suggestion.id} className="p-4 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-primary-300 dark:hover:border-primary-700 transition-all">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${suggestion.impact === 'high' ? 'bg-green-100 dark:bg-green-900/50' : 'bg-yellow-100 dark:bg-yellow-900/50'}`}>
                      <Lightbulb className={`w-5 h-5 ${suggestion.impact === 'high' ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">{suggestion.title}</h4>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${suggestion.impact === 'high' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300'}`}>
                          {suggestion.impact === 'high' ? t('automation.ai.highImpact', 'High impact') : t('automation.ai.mediumImpact', 'Medium impact')}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          {suggestion.platform === 'google' ? <GoogleAdsIcon /> : suggestion.platform === 'meta' ? <MetaAdsIcon /> : <LinkedInAdsIcon />}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{suggestion.description}</p>
                    </div>
                  </div>
                  <button onClick={() => onCreateFromSuggestion(suggestion)} className="btn-primary btn-sm whitespace-nowrap">
                    {t('automation.ai.createRule', 'Create Rule')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AutomationAISuggestions;
